var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ACCOUNT_NOT_SET_ERROR_MESSAGE, IGNORED_ERROR_MESSAGES } from "../utils/constants";
import { tokenSymbolToCurrency, newMonetaryAmount } from "../utils";
export class DefaultTransactionAPI {
    constructor(api, account) {
        this.api = api;
        this.account = account;
    }
    setAccount(account) {
        this.account = account;
    }
    removeAccount() {
        this.account = undefined;
    }
    getAccount() {
        return this.account;
    }
    sendLogged(transaction, successEventType, extrinsicStatus) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.account === undefined) {
                return Promise.reject(new Error(ACCOUNT_NOT_SET_ERROR_MESSAGE));
            }
            return DefaultTransactionAPI.sendLogged(this.api, this.account, transaction, successEventType, extrinsicStatus);
        });
    }
    getFeeEstimate(extrinsic) {
        return __awaiter(this, void 0, void 0, function* () {
            const nativeCurrency = tokenSymbolToCurrency(this.api.consts.currency.getNativeCurrencyId.asToken);
            const account = this.account;
            if (account === undefined) {
                return newMonetaryAmount(0, nativeCurrency);
            }
            const paymentInfo = yield extrinsic.paymentInfo(account);
            return newMonetaryAmount(paymentInfo.partialFee.toString(), nativeCurrency);
        });
    }
    dryRun(extrinsic) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.account === undefined) {
                return Promise.reject(new Error(ACCOUNT_NOT_SET_ERROR_MESSAGE));
            }
            try {
                yield extrinsic.dryRun(this.account);
                return { success: true };
            }
            catch (error) {
                return { success: false, error };
            }
        });
    }
    buildBatchExtrinsic(extrinsics, atomic = true) {
        return DefaultTransactionAPI.buildBatchExtrinsic(this.api, extrinsics, atomic);
    }
    /**
     * Builds a submittable extrinsic to send other extrinsic in batch.
     *
     * @param api The ApiPromis instance to construct the batch extrinsic with.
     * @param extrinsics An array of extrinsics to be submitted as batch.
     * @param atomic Whether the given extrinsics should be handled atomically or not.
     *   When true (default) all extrinsics will rollback if one fails (batchAll), otherwise allows partial successes (batch).
     * @returns A batch/batchAll submittable extrinsic.
     */
    static buildBatchExtrinsic(api, extrinsics, atomic = true) {
        const batchExtrinsic = (atomic ? api.tx.utility.batchAll : api.tx.utility.batch)(extrinsics);
        return batchExtrinsic;
    }
    static sendLogged(api, account, transaction, successEventType, extrinsicStatus) {
        return __awaiter(this, void 0, void 0, function* () {
            const { unsubscribe, result } = yield new Promise((resolve, reject) => {
                let unsubscribe;
                // When passing { nonce: -1 } to signAndSend the API will use system.accountNextIndex to determine the nonce
                transaction
                    .signAndSend(account, { nonce: -1 }, (result) => callback({ unsubscribe, result }))
                    .then((u) => (unsubscribe = u))
                    .catch((error) => reject(error));
                let foundStatus = false;
                // only need to check this if we do want to wait for the success event
                let foundEvent = successEventType !== undefined;
                function callback(callbackObject) {
                    const status = callbackObject.result.status;
                    foundStatus =
                        foundStatus ||
                            (extrinsicStatus ? extrinsicStatus.type === status.type : status.isInBlock) ||
                            status.isFinalized;
                    foundEvent =
                        // if we found it before there is no need to check again
                        foundEvent ||
                            // if the event we are looking for is undefined, assume it has been found
                            successEventType === undefined ||
                            DefaultTransactionAPI.doesArrayContainEvent(callbackObject.result.events, successEventType);
                    if (foundStatus && foundEvent) {
                        resolve(callbackObject);
                    }
                }
            });
            if (result.status.isInBlock) {
                console.log(`Transaction included at blockHash ${result.status.asInBlock}`);
            }
            else if (result.status.isFinalized) {
                console.log(`Transaction finalized at blockHash ${result.status.asFinalized}`);
            }
            unsubscribe();
            // Print all events for debugging
            DefaultTransactionAPI.printEvents(api, result.events);
            const dispatchError = result.dispatchError;
            if (dispatchError) {
                // Construct error message
                let message = "The transaction failed.";
                // Runtime error in one of the parachain modules
                if (dispatchError.isModule) {
                    // for module errors, we have the section indexed, lookup
                    const decoded = api.registry.findMetaError(dispatchError.asModule);
                    const { docs, name, section } = decoded;
                    message = message.concat(` The error code is ${section}.${name}. ${docs.join(" ")}`);
                    // Bad origin
                }
                else if (dispatchError.isBadOrigin) {
                    message = message.concat(` The error is caused by using an incorrect account.
                The error code is BadOrigin ${dispatchError}.`);
                }
                // Other, CannotLookup, no extra info
                else {
                    message = message.concat(` The error is ${dispatchError}.`);
                }
                console.log(message);
                return Promise.reject(new Error(message));
            }
            return result;
        });
    }
    static printEvents(api, events) {
        let foundErrorEvent = false;
        let errorMessage = "";
        events
            .map(({ event }) => event.data)
            .forEach((eventData) => {
            if (DefaultTransactionAPI.isDispatchError(eventData)) {
                try {
                    const { docs, name, section } = api.registry.findMetaError(eventData.asModule);
                    if ((docs === null || docs === void 0 ? void 0 : docs.length) > 0) {
                        errorMessage = `${section}.${name}: ${docs.join(" ")}`;
                    }
                    else {
                        errorMessage = `${section}.${name}`;
                    }
                    foundErrorEvent = true;
                }
                catch (err) {
                    errorMessage = "Error. Could not find transaction failure details.";
                }
            }
        });
        if (!foundErrorEvent) {
            events.forEach(({ phase, event: { data, method, section } }) => {
                console.log(`\t' ${phase}: ${section}.${method}:: ${data}`);
            });
        }
        else if (!IGNORED_ERROR_MESSAGES.includes(errorMessage)) {
            throw new Error(errorMessage);
        }
    }
    static isDispatchError(eventData) {
        return eventData.isModule !== undefined;
    }
    static doesArrayContainEvent(events, eventType) {
        for (const { event } of events) {
            if (eventType.is(event)) {
                return true;
            }
        }
        return false;
    }
}
//# sourceMappingURL=transaction.js.map